import { useState } from 'react';

export default function LeadCapture() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d9ammtijutvtlj0vtaig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg?.toLowerCase().includes('spam');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please try again.');
    }
  };

  return (
    <section id="resources" className="bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-accent-600 text-sm font-semibold tracking-widest uppercase mb-3">
              Free Resources
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-primary-950 max-w-3xl mx-auto leading-tight">
              Tools to Strengthen
              <span className="italic text-accent-600"> Your Finances</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Checklist Card */}
            <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/60">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-file-list-3-line text-accent-600 text-2xl" />
                </div>
                <div>
                  <h3 className="font-heading text-xl text-primary-950 font-semibold mb-1">
                    Richmond Area Restaurant Cost Control & Compliance Checklist
                  </h3>
                  <p className="text-foreground-500 text-sm">
                    Instant PDF — covers prime cost targets, sales tax setup, tipped wage
                    compliance, and POS reconciliation.
                  </p>
                </div>
              </div>

              <form data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                <input type="text" name="website_alt" className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="name"
                    placeholder="Full name"
                    required
                    className="bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Business email"
                    required
                    className="bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                  />
                </div>
                <select
                  name="business_type"
                  required
                  defaultValue=""
                  className="w-full bg-background-100 border border-background-200 rounded-lg px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-500/40"
                >
                  <option value="" disabled>Business type</option>
                  <option value="restaurant">Restaurant / Food Hospitality</option>
                  <option value="trades">Skilled Trades / Contractor</option>
                  <option value="small_business">Small Business</option>
                  <option value="nonprofit">Nonprofit</option>
                  <option value="both">Other</option>
                </select>
                <button
                  type="submit"
                  className="w-full bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-bold px-6 py-3.5 rounded-lg transition-all whitespace-nowrap"
                >
                  Download Free Checklist
                  <i className="ri-download-2-line ml-2" />
                </button>
              </form>
              {status === 'success' && (
                <p className="mt-3 text-accent-600 text-sm font-medium">
                  Checklist sent! Check your inbox.
                </p>
              )}
              {status === 'error' && formError && (
                <p className="mt-3 text-red-600 text-sm">{formError}</p>
              )}
            </div>

            {/* Audit Template Card */}
            <div className="bg-primary-950 rounded-2xl p-6 md:p-8">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center shrink-0">
                  <i className="ri-calculator-line text-accent-300 text-2xl" />
                </div>
                <div>
                  <h3 className="font-heading text-xl text-background-50 font-semibold mb-1">
                    Skilled Trades Job Profitability & Cash Flow Audit Template
                  </h3>
                  <p className="text-background-400 text-sm">
                    Excel template to analyze per-project margins, overhead allocation,
                    cash flow gaps, and retainage impact.
                  </p>
                </div>
              </div>

              <ul className="space-y-2.5 mb-6">
                {[
                  'Per-project P&L breakdown with overhead allocation',
                  'Cash flow gap identification by month',
                  'Retainage aging and collection timeline',
                  'Material markup vs. actual margin analysis',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-background-300 text-sm">
                    <i className="ri-check-line text-accent-400 text-sm mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  const el = document.querySelector('#contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-shine w-full bg-background-50 hover:bg-accent-100 text-primary-950 text-sm font-bold px-6 py-3.5 rounded-lg transition-all whitespace-nowrap"
              >
                Request Template
                <i className="ri-arrow-right-line ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}